import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import * as immutable from 'object-path-immutable';
import { Button } from '@companycam/slab-web';

import toast from 'helpers/toast';

import Form from './Form';
import {
  DELETE_REPORT_TEXT_SNIPPET,
  TEXT_SNIPPET_QUERY,
} from '../gql/snippets.graphql';

defineMessages({
  deleteSuccessMsg: {
    id: 'SnippetEditor.delete.deleteSuccessMsg',
    defaultMessage: 'Snippet Successfully Deleted',
    description: 'Snippet delete success message',
  },
  deleteErrorMsg: {
    id: 'SnippetEditor.delete.deleteErrorMsg',
    defaultMessage: 'Could Not Delete Snippet. Please try again',
    description: 'Snippet delete error message',
  },
});

const Delete = ({ snippet, handleCancel, intl }) => {
  const { formatMessage } = intl;

  const [deleteSnippet] = useMutation(DELETE_REPORT_TEXT_SNIPPET, {
    update: (cache, result) => {
      const oldQueryData = cache.readQuery({
        query: TEXT_SNIPPET_QUERY,
      });

      const newQueryData = immutable.set(
        oldQueryData,
        'viewer.company.reportsTextSnippet.nodes',
        oldQueryData.viewer.company.reportsTextSnippet.nodes.filter(
          (filteredSnippet) =>
            filteredSnippet.id !==
            result.data.reportTextSnippetDelete.deletedId,
        ),
      );

      cache.writeQuery({
        query: TEXT_SNIPPET_QUERY,
        data: newQueryData,
      });
    },
    onCompleted: () => {
      toast.success(
        formatMessage({ id: 'SnippetEditor.delete.deleteSuccessMsg' }),
      );
      handleCancel(true);
    },
    onError: () => {
      toast.error(formatMessage({ id: 'SnippetEditor.delete.deleteErrorMsg' }));
    },
  });

  const handleDeleteSnippet = () => {
    const input = {
      variables: {
        input: {
          id: snippet.id,
        },
      },
    };

    deleteSnippet(input);
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const FormHeader = () => (
    <React.Fragment>
      <h3>
        <FormattedMessage
          id="SnippetEditor.delete.header"
          defaultMessage="Edit Snippet"
          description="Delete snippet header"
        />
      </h3>

      <div>
        <Button
          size="small"
          onClick={() => handleCancel(false)}
          style={{ marginRight: 8 }}
        >
          <FormattedMessage id="Common.cancel" />
        </Button>
        <Button color="destroy" size="small" type="submit">
          {formatMessage({ id: 'Common.confirmDelete' })}
        </Button>
      </div>
    </React.Fragment>
  );

  return (
    <Formik
      initialValues={snippet}
      onSubmit={handleDeleteSnippet}
      enableReinitialize
    >
      {(formikProps) => (
        <Form
          {...formikProps}
          originalTags={snippet.tags.nodes}
          originalContent={snippet.content}
          isDeleting
        >
          <FormHeader />
        </Form>
      )}
    </Formik>
  );
};

Delete.propTypes = {
  snippet: PropTypes.object,
  handleCancel: PropTypes.func,
  intl: PropTypes.any,
};

export default injectIntl(Delete);
